import React from 'react';
import _ from 'underscore';

import ProteinAlignmentModal from 'analyses/aa-sequence-alignment/AaSequenceAlignmentModal';
import specifierForProtein from 'analyses/aa-sequence-alignment/util/specifierForAaSequence';
import DataConnector from 'connect/DataConnector';
import {ProteinAlignment} from 'constants/AlignmentConstants';
import ProteinAlignmentStore from 'molbio/aa-sequences/AaSequenceAlignmentStore';
import ProteinStore from 'molbio/aa-sequences/AaSequenceStore';
import {ProteinAlignmentSummary} from 'molbio/aa-sequences/util/AaSequenceConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import ProteinAlignmentPanelTable from 'toolbar/aa-sequence/AaSequenceAlignmentPanelTable';

interface ProteinAlignmentPanelProps {
  proteinId: string;
  closePanel: () => void;
}

interface EnhancedProps {
  alignments: Array<ProteinAlignment | ProteinAlignmentSummary>;
  linkedAlignments: Array<ProteinAlignment | ProteinAlignmentSummary>;
}

class ProteinAlignmentPanelConnector extends DataConnector<ProteinAlignmentPanelProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [ProteinAlignmentStore];

  _sortByCreatedAt(alignments: Array<ProteinAlignment>): Array<ProteinAlignment> {
    return _.sortBy(alignments, 'createdAt').reverse();
  }

  enhanceProps(): EnhancedProps {
    return {
      alignments: this._sortByCreatedAt(this.proteinAlignmentStore().getAlignments(this.props.proteinId)),
      linkedAlignments: this._sortByCreatedAt(
        this.proteinAlignmentStore().getLinkedAlignments(this.props.proteinId)
      ),
    };
  }
}

class ProteinAlignmentPanel extends React.Component<ProteinAlignmentPanelProps & EnhancedProps> {
  _allowFeatureEdits = (): boolean =>
    ProteinStore.getProteinById(this.props.proteinId)?.permissions.polymerFeaturesEditable ?? false;

  _onCreateAlignment = (): void => {
    const protein = ProteinStore.getProteinById(this.props.proteinId)!;
    ProteinAlignmentModal.openWithTemplateSpecifier(specifierForProtein(protein));
  };

  _renderAlignments = (): JSX.Element => (
    <div>
      <h5 className="u-spacing-top">Saved Alignments</h5>
      {this.props.alignments.length ? (
        <ProteinAlignmentPanelTable
          alignments={this.props.alignments}
          showActionButtons={this._allowFeatureEdits()}
          closePanel={this.props.closePanel}
          dataTestAttrs={{
            component: 'ProteinAlignmentPanel',
            element: 'proteinAlignmentTable',
          }}
        />
      ) : (
        <div className="u-small-spacing-top u-small-spacing-bottom">No saved protein alignments.</div>
      )}
    </div>
  );

  _renderLinkedAlignments = (): JSX.Element => (
    <div>
      <h5 className="u-spacing-top">Linked Alignments</h5>
      {this.props.linkedAlignments.length ? (
        <ProteinAlignmentPanelTable
          alignments={this.props.linkedAlignments}
          showActionButtons={false}
          closePanel={this.props.closePanel}
        />
      ) : (
        <div className="u-small-spacing-top u-small-spacing-bottom">No linked protein alignments.</div>
      )}
    </div>
  );

  override render(): JSX.Element {
    return (
      <div {...getTestProps({component: 'ProteinAlignmentPanel', element: 'root'})}>
        <button
          className="btn btn-af-primary btn-block u-small-spacing-top"
          onClick={this._onCreateAlignment}
          disabled={!this._allowFeatureEdits()}
          type="button"
        >
          Create New Alignment
        </button>
        {this._renderAlignments()}
        {this._renderLinkedAlignments()}
      </div>
    );
  }
}

export default ProteinAlignmentPanelConnector.connect(ProteinAlignmentPanel);
